import { useParams } from "react-router";
import { useMemo, useCallback } from "react";
import { Skeleton } from "@/components/ui/Skeleton";
import { useProgramaById } from "@/hooks/programas/useProgramas";
import { useAprendicesConActividades } from "@/hooks/programas/useGestionAprendices";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { Separator } from "@/components/ui/separator";
import type { ActividadPrograma } from "@/interfaces/actividadPrograma.interface";

import { ProgramaHeader } from "./ProgramaHeader";
import { ProgramaActividades } from "./ProgramaActividades";
import { ProgramaDocumentos } from "./ProgramaDocumentos";
import { ProgramaAprendices } from "./ProgramaAprendices";
import { ProgramaReportes, useReportesState } from "./ProgramaReportes";

export default function ProgramaDetalles() {
  const { id } = useParams();
  const idPrograma = Number(id);
  const { data: programa, isLoading, refetch } = useProgramaById(idPrograma);
  const { user } = useCurrentUser();
  const isSuperAdmin = user?.rol?.nombre === "SUPER_ADMIN";

  // Shared data: aprendices are needed by both Header (totalAsignaciones) and Actividades (counts per activity)
  const { data: datosAprendices } = useAprendicesConActividades(idPrograma, true);

  const aprendices = datosAprendices?.aprendices || [];

  const totalAsignaciones = useMemo(() => {
    return aprendices.reduce(
      (acc, aprendiz) => acc + aprendiz.actividades.length,
      0,
    );
  }, [aprendices]);

  // Report state (currently disabled, but keeping state management ready)
  const reportes = useReportesState();

  const handleActividadReporte = useCallback(
    (actividad: ActividadPrograma) => {
      reportes.openReporteActividad(actividad);
    },
    [reportes],
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-6 w-80" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!programa) {
    return (
      <div className="rounded-2xl border border-black/5 bg-[var(--card-background)] p-6 text-center text-sm text-muted-foreground dark:border-white/10">
        No se encontró información del programa.
      </div>
    );
  }

  return (
    <div className="space-y-8 sm:space-y-10 md:space-y-12">
      <ProgramaHeader
        programa={programa}
        totalAsignaciones={totalAsignaciones}
        onOpenReporte={reportes.openReportePrograma}
      />

      <ProgramaActividades
        programa={programa}
        onRefetch={refetch}
        aprendices={aprendices}
        isSuperAdmin={isSuperAdmin}
        onActividadReporte={handleActividadReporte}
      />

      <Separator className="my-8" />

      <ProgramaAprendices
        idPrograma={idPrograma}
        programa={programa}
        isSuperAdmin={isSuperAdmin}
      />

      <Separator className="my-8" />

      <ProgramaDocumentos
        idPrograma={idPrograma}
        cerebro={programa.cerebro ?? ""}
        onRefetchPrograma={refetch}
      />

      <ProgramaReportes idPrograma={idPrograma} programa={programa} />
    </div>
  );
}
